import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { Provider } from "jotai";
import { Suspense, useCallback, useEffect, useState } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { ClubStackNavigatorParamList } from "../../navigators/ClubNavigator";
import {
  ConfirmPanel,
  ConfirmPanelQueryString,
} from "./ConfirmPanel/ConfirmPanel";
import { ConfirmPanelQuery } from "./ConfirmPanel/__generated__/ConfirmPanelQuery.graphql";
import { InvitePanel } from "./InvitePanel/InvitePanel";
import { JoinBookingScreenQuery } from "./__generated__/JoinBookingScreenQuery.graphql";
import { JoinBookingScreenSessionQuery } from "./__generated__/JoinBookingScreenSessionQuery.graphql";
import { useHandleInvalidShareId } from "./hooks/useHandleInvalidShareId/useHandleInvalidShareId";
import { useHandleUserAlreadyInBooking } from "./hooks/useHandleUserAlreadyInBooking/useHandleUserAlreadyInBooking";

export const JoinBookingScreen = () => {
  const [sessionQueryRef, loadSessionQuery] =
    useQueryLoader<JoinBookingScreenSessionQuery>(
      JoinBookingScreenSessionQueryString
    );

  useEffect(() => {
    loadSessionQuery({}, { fetchPolicy: "store-and-network" });
  }, [loadSessionQuery]);

  return (
    <Suspense>
      {sessionQueryRef ? (
        <JoinBookingScreenSessionQueryRenderer
          sessionQueryRef={sessionQueryRef}
        />
      ) : null}
    </Suspense>
  );
};

const JoinBookingScreenSessionQueryRenderer = ({
  sessionQueryRef,
}: {
  sessionQueryRef: PreloadedQuery<JoinBookingScreenSessionQuery>;
}) => {
  const data = usePreloadedQuery(
    JoinBookingScreenSessionQueryString,
    sessionQueryRef
  );
  const membershipTypeId =
    data.session?.currentClientProfile?.member?.membershipType?.id ?? "";
  const [queryRef, loadQuery] = useQueryLoader<JoinBookingScreenQuery>(
    JoinBookingScreenQueryString
  );

  const route =
    useRoute<RouteProp<ClubStackNavigatorParamList, "JoinBooking">>();

  const { joinableId } = route.params;

  useEffect(() => {
    loadQuery({ shareId: joinableId, membershipTypeId });
  }, [loadQuery, joinableId, membershipTypeId]);
  return (
    <Suspense>
      {queryRef ? (
        <JoinBookingScreenQueryRenderer
          queryRef={queryRef}
          sessionQueryRef={sessionQueryRef}
          membershipTypeId={membershipTypeId}
        />
      ) : null}
    </Suspense>
  );
};

const JoinBookingScreenQueryRenderer = ({
  queryRef,
  sessionQueryRef,
  membershipTypeId,
}: {
  queryRef: PreloadedQuery<JoinBookingScreenQuery>;
  sessionQueryRef: PreloadedQuery<JoinBookingScreenSessionQuery>;
  membershipTypeId: string;
}) => {
  const [confirmPanelQueryRef, loadQuery] = useQueryLoader<ConfirmPanelQuery>(
    ConfirmPanelQueryString
  );

  const [hasUserConfirmed, setHasUserConfirmed] = useState(false);
  const data = usePreloadedQuery(JoinBookingScreenQueryString, queryRef);
  const sessionData = usePreloadedQuery(
    JoinBookingScreenSessionQueryString,
    sessionQueryRef
  );
  const club = sessionData.session?.currentClientProfile?.member?.club;
  const clubSlug = club?.slug;

  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();
  const navigateHome = useCallback(() => {
    if (!!clubSlug) {
      navigation.navigate("Home", { clubSlug, screen: "MainHome" });
    }
  }, [clubSlug, navigation]);

  useHandleInvalidShareId(clubSlug, !!data.sharedBooking);
  useHandleUserAlreadyInBooking(
    data.sharedBooking ?? null,
    sessionData.session ?? null
  );

  useEffect(() => {
    if (hasUserConfirmed) {
      loadQuery({}, { fetchPolicy: "store-and-network" });
    }
  }, [loadQuery, hasUserConfirmed]);

  return (
    <>
      {hasUserConfirmed ? (
        <Provider>
          {confirmPanelQueryRef ? (
            <ConfirmPanel
              navigateHome={navigateHome}
              bookingRef={data.sharedBooking ?? null}
              sessionQueryRef={confirmPanelQueryRef}
              membershipTypeId={membershipTypeId}
            />
          ) : null}
        </Provider>
      ) : (
        <InvitePanel
          bookingRef={data.sharedBooking ?? null}
          sessionRef={sessionData.session ?? null}
          onConfirm={() => {
            setHasUserConfirmed(true);
          }}
          navigateHome={navigateHome}
        />
      )}
    </>
  );
};

const JoinBookingScreenQueryString = graphql`
  query JoinBookingScreenQuery($shareId: String!, $membershipTypeId: ID!) {
    sharedBooking(shareId: $shareId) {
      ...InvitePanel_Booking
      ...useHandleUserAlreadyInBooking_Booking
      ...ConfirmPanel_Booking
    }
  }
`;

const JoinBookingScreenSessionQueryString = graphql`
  query JoinBookingScreenSessionQuery {
    session {
      ...InvitePanel_Session
      ...useHandleUserAlreadyInBooking_Session
      currentClientProfile {
        __typename
        member {
          membershipType {
            id
          }
          club {
            slug
          }
        }
      }
    }
  }
`;
